import React, { useState } from 'react';
import { UseFormRegister, UseFormSetValue, FieldErrors, Path, FieldValues, Control, useWatch } from 'react-hook-form';
import { AddressAutocomplete } from './AddressAutocomplete';
import { Input } from '@/app/components/ui/Input';
import { Label } from '@/app/components/ui/Label';
import { cn } from '@/lib/utils';

interface AddressSectionProps<T extends FieldValues> {
    prefix: string;
    label?: string;
    labelClassName?: string;
    register: UseFormRegister<T>;
    setValue: UseFormSetValue<T>;
    errors: FieldErrors<T>;
    control: Control<T>;
}

export const AddressSection = <T extends FieldValues>({
    prefix,
    label,
    labelClassName,
    register,
    setValue,
    errors,
    control,
}: AddressSectionProps<T>) => {
    const [isEditing, setIsEditing] = useState(false);
    const [needsLine1, setNeedsLine1] = useState(false);

    // Watch current address values
    // We cast prefix as any because T is generic but prefix path matches the schema structure
    const currentAddress = useWatch({
        control,
        name: prefix as any,
    });

    // Check if we have data (line1 or postcode implies data)
    const hasData = !!(currentAddress?.line1 || currentAddress?.postcode);

    // States:
    // 1. Empty & Not Editing -> Show Autocomplete + Manual Link
    // 2. Data & Not Editing -> Show Summary Card
    // 3. Editing -> Show Inputs

    const showSummary = hasData && !isEditing;
    const showInputs = isEditing;
    const showSearch = !hasData && !isEditing;

    // Helper to get error safely
    const getError = (field: string) => {
        const anyErrors = errors as any;
        const group = anyErrors[prefix];

        // Accessing deep error:
        if (prefix.includes('.')) {
            const parts = prefix.split('.');
            let current: any = anyErrors;
            for (const part of parts) {
                current = current?.[part];
            }
            return current?.[field];
        }

        return group?.[field];
    };

    const handleAddressSelect = (address: {
        name: string;
        line1: string;
        line2: string;
        city: string;
        postcode: string;
    }) => {
        setValue(`${prefix}.name` as Path<T>, address.name as any, { shouldValidate: true });
        setValue(`${prefix}.line1` as Path<T>, address.line1 as any, { shouldValidate: true });
        setValue(`${prefix}.line2` as Path<T>, address.line2 as any, { shouldValidate: true });
        setValue(`${prefix}.city` as Path<T>, address.city as any, { shouldValidate: true });
        setValue(`${prefix}.postcode` as Path<T>, address.postcode as any, { shouldValidate: true });
        setNeedsLine1(false);
        setIsEditing(false); // Go to summary view
    };

    const handleIncompleteAddress = (address: {
        name?: string;
        line1?: string;
        line2?: string;
        city?: string;
        postcode?: string;
    }) => {
        // Prefill what we can, keep them in edit mode and require line1
        setValue(`${prefix}.name` as Path<T>, (address.name || '') as any, { shouldValidate: true });
        setValue(`${prefix}.line1` as Path<T>, '' as any, { shouldValidate: true });
        setValue(`${prefix}.line2` as Path<T>, (address.line2 || '') as any, { shouldValidate: true });
        setValue(`${prefix}.city` as Path<T>, (address.city || '') as any, { shouldValidate: true });
        setValue(`${prefix}.postcode` as Path<T>, (address.postcode || '') as any, { shouldValidate: true });
        setNeedsLine1(true);
        setIsEditing(true);
    };

    const handleClear = () => {
        setValue(`${prefix}.name` as Path<T>, '' as any);
        setValue(`${prefix}.line1` as Path<T>, '' as any);
        setValue(`${prefix}.line2` as Path<T>, '' as any);
        setValue(`${prefix}.city` as Path<T>, '' as any);
        setValue(`${prefix}.postcode` as Path<T>, '' as any);
        setIsEditing(false); // Back to search
    };

    return (
        <div className="space-y-3 animate-fade-in relative z-50">
            {label && <Label className={cn("text-lg", labelClassName)}>{label}</Label>}

            <div className="space-y-3">
                {showSearch && (
                    <div className="animate-fade-in relative z-50">
                        <AddressAutocomplete
                            onAddressSelect={handleAddressSelect}
                            onIncompleteAddress={handleIncompleteAddress}
                        />
                        <button
                            type="button"
                            className="text-sm text-primary font-medium hover:underline focus:outline-none mt-2"
                            onClick={() => setIsEditing(true)}
                        >
                            Enter address manually
                        </button>
                    </div>
                )}

                {showSummary && (
                    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 flex justify-between items-start animate-fade-in group">
                        <div className="text-sm text-gray-700">
                            {currentAddress?.name && <p className="font-semibold">{currentAddress.name}</p>}
                            <p className={currentAddress?.name ? '' : 'font-medium'}>{currentAddress?.line1}</p>
                            {currentAddress?.line2 && <p>{currentAddress.line2}</p>}
                            <p>{currentAddress?.city}</p>
                            <p>{currentAddress?.postcode}</p>
                        </div>
                        <div className="flex flex-col gap-2">
                            <button
                                type="button"
                                onClick={() => setIsEditing(true)}
                                className="text-xs font-medium text-primary hover:text-primary/80 border border-primary/20 bg-white hover:bg-primary/5 px-3 py-1.5 rounded transition-colors"
                            >
                                Edit
                            </button>
                            <button
                                type="button"
                                onClick={handleClear}
                                className="text-xs text-gray-400 hover:text-destructive transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                )}

                {showInputs && (
                    <div className="animate-fade-in space-y-3">
                        <div>
                            <Input {...register(`${prefix}.name` as Path<T>)} placeholder="Company / Building Name (Optional)" />
                        </div>

                        <div>
                            <Input {...register(`${prefix}.line1` as Path<T>)} placeholder="Address Line 1" />
                            {(getError('line1') || needsLine1) && (
                                <p className="text-destructive text-sm mt-1">
                                    {getError('line1')?.message || 'Please enter Address Line 1.'}
                                </p>
                            )}
                        </div>

                        <div>
                            <Input {...register(`${prefix}.line2` as Path<T>)} placeholder="Address Line 2 (Optional)" />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Input {...register(`${prefix}.city` as Path<T>)} placeholder="City" />
                                {getError('city') && <p className="text-destructive text-sm mt-1">{getError('city').message}</p>}
                            </div>
                            <div>
                                <Input {...register(`${prefix}.postcode` as Path<T>)} placeholder="Postcode" />
                                {getError('postcode') && <p className="text-destructive text-sm mt-1">{getError('postcode').message}</p>}
                            </div>
                        </div>

                        {hasData && (
                            <div className="pt-2 flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => setIsEditing(false)}
                                    className="text-xs font-medium text-primary hover:underline"
                                >
                                    Done
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

